<template>
   <!-- country flags + radio choice -->
   <div class="mt-12 flex justify-center">
      <!-- UK -->
      <div class="relative w-20">
         <input
            id="uk"
            v-model="state.country"
            class="absolute inset-0 opacity-0"
            name="delivery-instructions-country"
            type="radio"
            value="uk"
         />
         <label class="relative block cursor-pointer" for="uk">
            <span class="relative mb-3 block w-20">
               <IconFlagUK />
            </span>
            <span
               :class="[state.country === 'uk' ? 'block' : 'hidden']"
               class="mx-auto flex size-3 items-center justify-center rounded-full border"
            >
               <span class="rounded-full bg-brand-dark" style="width: 6px; height: 6px" />
            </span>
            <span class="sr-only">{{
               $t("dna-testing.country_instructions", { country: state.country.toLocaleUpperCase() })
            }}</span>
         </label>
      </div>
      <!-- USA -->
      <div class="relative ml-6 w-20">
         <input
            id="us"
            v-model="state.country"
            class="absolute inset-0 opacity-0"
            name="delivery-instructions-country"
            type="radio"
            value="us"
         />
         <label class="relative block cursor-pointer" for="us">
            <span class="relative mb-3 block w-20">
               <IconFlagUS />
            </span>
            <span
               :class="[state.country === 'us' ? 'block' : 'hidden']"
               class="mx-auto flex size-3 items-center justify-center rounded-full border"
            >
               <span class="rounded-full bg-brand-dark" style="width: 6px; height: 6px" />
            </span>
            <span class="sr-only">{{
               $t("dna-testing.country_instructions", { country: state.country.toLocaleUpperCase() })
            }}</span>
         </label>
      </div>
   </div>

   <!-- Options total count -->
   <p class="mx-auto mt-8 max-w-xs text-center text-lg leading-tight text-black">
      <i18n-t keypath="dna-testing.return_options_message" tag="p" scope="global">
         <template v-slot:ways>
            <span class="text-bolder">{{ $t("dna-testing.ways", { count: numOfOptions }) }}</span>
         </template>
      </i18n-t>
   </p>

   <!-- Options available to return sample -->
   <transition-group name="sano-fade">
      <div
         v-for="(option, option_i) in currentInstructions"
         :key="`${state.country}-option-${option_i}`"
         class="h-full"
      >
         <!-- larger screen mode -->
         <div
            :style="`color: ${textColour}; background: ${bgColour}`"
            class="text-regular mt-8 hidden rounded-lg p-12 leading-loose md:block lg:mt-10 lg:flex xl:p-16"
         >
            <div class="flex lg:w-5/12 lg:max-w-xs lg:flex-col">
               <!-- Country name -->
               <div class="pr-6">
                  <span class="text-bolder block pb-1">
                     <span>{{ $t("dna-testing.country_only", { country: state.country.toUpperCase() }) }}</span>
                  </span>
                  <h3 class="text-3xl">{{ $t(option.header) }}</h3>
               </div>

               <!-- delivery logo -->
               <div class="mt-6 max-w-xs">
                  <IconShippingProviderLogo :provider="shippingProvider" class="h-8 w-6 shrink-0 lg:h-10 lg:w-8" />
               </div>
            </div>

            <!-- Return steps -->
            <div class="max-w-md text-lg leading-loose lg:ml-auto lg:w-7/12">
               <ol>
                  <li v-for="(step, index) in option.steps" :key="`uk_ups_dropoff_steps-${index}`" class="flex px-0">
                     <div v-if="shouldShowNumber(option_i)" class="sano-regular block w-10 shrink-0">
                        {{ index + 1 }}.
                     </div>
                     <InstructionStep v-bind="step" />
                  </li>
               </ol>
            </div>
         </div>
         <!-- larger screen mode end -->

         <!-- small screen slides mode -->
         <!-- On mobile we are showing a separate card per step - add surrounding html to pass contents to the carousel. -->
         <div class="mt-5 flex flex-col rounded-lg p-3 md:hidden" :style="`background: ${bgColour};`">
            <Carousel
               :name="`${state.country}-option-${option_i}-carousel`"
               :totalSlides="numOfSlides(option_i)"
               :updateCurrentSlideTo="state.currentSlides[option_i]"
               @current-slide-change-to="
                  (val) => {
                     state.currentSlides[option_i] = val;
                  }
               "
            >
               <template v-for="(n, index) in numOfSlides(option_i)" :key="`card-${index}`" v-slot:[`slot-${index}`]>
                  <div class="flex w-full flex-col items-center" :style="`color: ${textColour}; min-height: inherit`">
                     <!-- Logo and slide number -->
                     <div class="flex w-full flex-row items-center justify-between">
                        <IconShippingProviderLogo :provider="shippingProvider" class="h-8 w-6" />
                        <span v-if="index > 0">{{ `${n}/${option.steps.length + 1}` }}</span>
                     </div>

                     <!-- Header slide -->
                     <div
                        v-if="index === 0"
                        class="flex w-full max-w-md flex-col justify-evenly text-center"
                        style="min-height: inherit"
                     >
                        <div class="flex flex-col">
                           <span class="mt-3 text-sm">{{
                              $t("dna-testing.country_only", { country: state.country.toUpperCase() })
                           }}</span>
                           <h3 class="text-regular mt-5 text-xl">{{ $t(option.header) }}</h3>
                        </div>
                        <div class="mt-3 text-center text-sm">{{ $t("dna-testing.swipe_to_start") }}</div>
                     </div>

                     <!-- Instruction slide -->
                     <div
                        v-else
                        class="flex w-full max-w-md flex-col items-center justify-evenly"
                        style="min-height: inherit"
                     >
                        <InstructionStep class="mt-3 h-full text-center" v-bind="option.steps[index - 1]" />
                        <div v-if="index < option.steps.length" class="mt-5 text-center text-sm">
                           {{ $t("dna-testing.swipe_to_continue") }}
                        </div>
                     </div>
                  </div>
               </template>
            </Carousel>
            <!--  Round buttons -->
            <div class="mt-10 flex w-full flex-row items-center justify-center">
               <button
                  v-for="(n, i) in numOfSlides(option_i)"
                  :key="`carousel-button-${i}`"
                  class="active mr-5 size-3 rounded-full border"
                  :style="`border-color: ${textColour}; background-color: ${
                     state.currentSlides[option_i] === i ? textColour : ''
                  }; transition: background-color 0.3s ease-in-out;`"
                  :aria-label="$t('system.carousel_slide_button', { slide_number: i + 1 })"
                  @click="switchCurrent(i, option_i)"
               ></button>
            </div>
         </div>
      </div>
      <!-- small screen slides mode end -->
   </transition-group>
</template>

<script setup lang="ts">
import InstructionStep from "./InstructionStep.vue";

const INSTRUCTIONS = {
   uk: [
      {
         header: "dna-testing.instructions.uk.1.header",
         steps: [
            { path: "dna-testing.instructions.uk.1.1", linkHref: "https://www.ups.com/gb/en/Home.page?" },
            { path: "dna-testing.instructions.uk.1.2", numOfTextBold: 1 },
            { path: "dna-testing.instructions.uk.1.3", numOfTextBold: 1 },
            { path: "dna-testing.instructions.uk.1.4", numOfTextBold: 1 },
            { path: "dna-testing.instructions.uk.1.5" },
         ],
      },
      {
         header: "dna-testing.instructions.uk.2.header",
         steps: [
            { path: "dna-testing.instructions.uk.2.1", linkHref: "https://wwwapps.ups.com/pickup/schedule?loc=en_GB" },
            {
               path: "dna-testing.instructions.uk.2.2",
               numOfTextBold: 2,
               imgSrc:
                  "https://media.sanogenetics.com/pages/register-test/instruction-screen-shots/uk/collect-step-02-w948-h546.png",
            },
            {
               path: "dna-testing.instructions.uk.2.3",
               numOfTextBold: 2,
               imgSrc:
                  "https://media.sanogenetics.com/pages/register-test/instruction-screen-shots/uk/collect-step-03-w948-h730.png",
            },
            {
               path: "dna-testing.instructions.uk.2.4",
               numOfTextBold: 4,
               imgSrc:
                  "https://media.sanogenetics.com/pages/register-test/instruction-screen-shots/uk/collect-step-04-w948-h874.png",
            },
            { path: "dna-testing.instructions.uk.2.5", numOfTextBold: 2 },
         ],
      },
   ],
   us: [
      {
         header: "dna-testing.instructions.us.1.header",
         steps: [
            { path: "dna-testing.instructions.us.1.1", linkHref: "https://www.usps.com/" },
            { path: "dna-testing.instructions.us.1.2", numOfTextBold: 1 },
            { path: "dna-testing.instructions.us.1.3", numOfTextBold: 1 },
            { path: "dna-testing.instructions.us.1.4", numOfTextBold: 2 },
            { path: "dna-testing.instructions.us.1.5" },
         ],
      },
      {
         header: "dna-testing.instructions.us.2.header",
         steps: [
            { path: "dna-testing.instructions.us.2.1", numOfTextBold: 2 },
            { path: "dna-testing.instructions.us.2.2", numOfTextBold: 2 },
         ],
      },
   ],
};

const state = reactive({
   country: "uk",
   currentSlides: [],
});

const currentInstructions = computed(() => INSTRUCTIONS[state.country]);

const numOfOptions = computed(() => currentInstructions.value.length);

const shippingProvider = computed(() => (state.country === "uk" ? "ups" : "usps"));

const bgColour = computed(() => (state.country === "uk" ? "#FFF6DF" : "#EDEDFA"));

const textColour = computed(() => (state.country === "uk" ? "#54223B" : "#54223B"));

// Set carousel's current slides to the first slide
state.currentSlides = new Array(numOfOptions.value).fill(0);

watch(
   () => state.country,
   (selectedCountry) => {
      // When the selected country changes, re-start instructions from the first carousel's slides.
      state.currentSlides = new Array(INSTRUCTIONS[selectedCountry].length).fill(0);
   }
);

const shouldShowNumber = (option_index) => {
   // method used to decide whether to display a number in front of the step
   return !(state.country === "us" && option_index === 1);
};

const numOfSlides = (optionIndex) => {
   return currentInstructions.value[optionIndex].steps.length + 1; // the +1 is for the header
};

const switchCurrent = (i, slider_i) => {
   // console.log(i, slider_i);
   state.currentSlides[slider_i] = i;
};
</script>

<style scoped lang="scss">
@use "@/assets/mixins.scss" as mixins;

input:focus-visible + label {
   @include mixins.focus-outline;
}

@supports not selector(:focus-visible) {
   input:focus + label {
      @include mixins.focus-outline;
   }
}

.sano-fade-fix-vertical-enter-active,
.sano-fade-enter-active {
   transition: opacity 0.45s ease-in-out 0.45s;
}

.sano-fade-fix-vertical-leave-active,
.sano-fade-leave-active {
   transition: opacity 0.45s ease-in-out;
}

.sano-fade-fix-vertical-leave-active,
.sano-fade-fix-vertical-enter-active {
   position: absolute !important;
   top: 50%;
   transform: translateY(-50%);
}

.sano-fade-fix-vertical-leave-to,
.sano-fade-fix-vertical-enter,
.sano-fade-leave-to,
.sano-fade-enter {
   opacity: 0;
}
</style>
